// ============================================
// WOMEN'S SEXUAL HEALTH / LIBIDO QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Women Sexual Health/Libido Questionnaire" (2025).
// Visit Type: womenSexualHealth
// Labs required: none. Follow-up cadence: every 6 months.
// Products: PT-141 (Bremelanotide), PDE5i (tadalafil/sildenafil/vardenafil),
//           Oxytocin.
//
// Color legend (Beluga source of truth):
//   BLACK  — required Q/A questions
//   BLUE   — conditional questions (shown per the preceding answer)
//   PURPLE — named-field mappings (medicalConditions / selfReportedMeds /
//            allergies / sex); excluded from the Q/A section by the backend
//   RED    — fully disqualifying answers
//   PINK   — product-routing instructions (drive patientPreference); here the
//            only routing rule is the PT-141 contraindication
//
// See docs/BACKEND_WOMEN_SEXUAL_HEALTH_VISIT_PRD.md for the canonical contract.

// ============================================
// CONSENT BODIES (verbatim from the questionnaire)
// ============================================

const PREGNANCY_CONSENT_BODY = `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`;

const MAIN_CONSENT_BODY = `Use of Medications and Off-Label Prescribing

This consent form provides important information about medications and therapies that may be prescribed to support female sexual health, including treatments intended to improve libido, arousal, intimacy, or overall sexual well-being. Please read this information carefully, as it explains how these treatments are used, their potential benefits, risks, alternatives, and your responsibilities as a patient.

This consent form provides detailed information about PT-141 (Bremelanotide), oxytocin, PDE5i (tadalafil, sildenafil, vardenafil, etc.) and other medications prescribed to address low female libido. Some medications used to support female sexual health are prescribed off-label. Off-label use means that a medication is being prescribed for a purpose, population, dosage, or method of administration that is not specifically included in the FDA-approved labeling. Off-label prescribing is common in medical practice and is based on clinical judgment, available research, and patient-specific factors.

You understand and acknowledge that:
- Not all medications used for female sexual health have FDA approval specifically for libido or sexual function.
- Your healthcare provider has determined that the potential benefits may outweigh the risks based on your individual health history and reported symptoms.
- Outcomes cannot be guaranteed.

Female sexual health medications may be used to address concerns such as low sexual desire, reduced arousal, difficulty with intimacy, or related emotional or physiological factors.

Potential benefits may include:
- Improved sexual desire or interest
- Enhanced arousal or sexual responsiveness
- Improved intimacy and emotional connection
- Improved sexual satisfaction and quality of life

Response to treatment varies from person to person, and not all patients will experience the same benefits.

All medications carry potential risks and side effects. These may vary depending on the specific medication, formulation, dose, and individual sensitivity.

Common or Possible Side Effects May Include:
- Nausea or gastrointestinal upset
- Headache
- Flushing or warmth
- Dizziness or lightheadedness
- Fatigue
- Mood changes
- Changes in blood pressure or heart rate
- Injection-site or application-site reactions (if applicable)
- Skin changes or pigmentation changes (with certain therapies)

Rare but Serious Risks May Include:
- Significant changes in blood pressure
- Severe allergic reactions (including swelling of the face, lips, tongue, or throat)
- Chest pain, shortness of breath, or fainting
- Concerning mood or behavioral changes

Some formulations may contain inactive ingredients or carriers that can trigger allergic reactions. You agree to inform your healthcare provider of any known allergies, sensitivities, or prior adverse medication reactions before starting treatment.

Patients with certain medical conditions such as poorly controlled blood pressure, significant cardiovascular disease, or other conditions determined by your provider may not be appropriate candidates for certain female sexual health medications. If you experience severe or concerning symptoms, seek immediate medical attention.

To ensure safety and effectiveness, your treatment may include ongoing monitoring, which can involve:
- Follow-up visits
- Symptom assessments
- Review of side effects or treatment response
- Adjustment or discontinuation of therapy if needed

You agree to communicate promptly about any new or worsening symptoms.

You understand that medication is not the only option for addressing female sexual health concerns. Alternatives may include:
- Counseling or sex therapy
- Relationship or couples therapy
- Lifestyle modifications (sleep, exercise, stress management)
- Addressing contributing medical or hormonal factors

You have the right to discuss these alternatives with your healthcare provider before or during treatment.

By proceeding with treatment, you agree to:
- Use medications only as prescribed
- Not exceed recommended doses or frequency
- Disclose all current medications, supplements, and relevant medical history
- Report side effects or concerns in a timely manner
- Participate in recommended follow-up and monitoring

By selecting below, you confirm that:
- You have read and understood the information in this consent
- You understand that some treatments may be prescribed off-label
- You acknowledge the potential benefits, risks, and limitations of treatment
- You agree to follow the prescribed plan and communicate openly with your healthcare provider
- You consent to proceed with treatment as discussed`;

// ============================================
// PURPLE — named-field questions (mapped server-side, excluded from Q/A)
// ============================================

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues.',
  placeholder: 'e.g., Hypertension, Asthma, Previous surgery, etc.',
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  placeholder: 'e.g., Lisinopril 10mg daily, Vitamin D 2000 IU daily…',
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  subtitle: 'This intake supports treatment for individuals assigned female at birth.',
  options: [
    { label: 'Male', value: 'male', disqualifies: true },
    { label: 'Female', value: 'female' },
  ],
};

// ---------- Pregnancy branch (conditional on female) ----------

const pregnancyStatusQuestion = {
  id: 'pregnancy-status',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

const pregnancyConsentQuestion = {
  id: 'pregnancy-consent',
  type: 'consent-dropdown',
  question: 'Pregnancy Acknowledgment',
  consentText: PREGNANCY_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the above information, I understand the risks and wish to proceed', value: 'consent' },
    { label: 'I have read the information and do NOT wish to proceed', value: 'decline', disqualifies: true },
  ],
};

// ---------- Vitals ----------

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'Your provider uses this to help personalize care.',
  skipBmiCheck: true,
};

// ---------- Sexual health concerns ----------

const sexualHealthConcernsQuestion = {
  id: 'sexual-health-concerns',
  type: 'multi-select',
  question: 'What are your primary concerns related to your sexual health?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Decreased sexual desire', value: 'decreased-desire' },
    { label: 'Difficulty becoming aroused', value: 'difficulty-arousal' },
    { label: 'Difficulty achieving orgasm', value: 'difficulty-orgasm' },
    { label: 'Pain during sexual activity', value: 'pain-during-activity' },
    { label: 'Other', value: 'other' },
  ],
};

const sexualHealthSymptomsDetailsQuestion = {
  id: 'sexual-health-symptoms-details',
  type: 'textarea',
  question: 'Tell us more about your sexual health symptoms',
  placeholder: 'Describe what you have been experiencing…',
};

const conditionsQuestion = {
  id: 'conditions',
  type: 'multi-select',
  question: 'Do you have any of the following conditions?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Pain with sex', value: 'pain-with-sex' },
    { label: 'Vaginal dryness', value: 'vaginal-dryness' },
    { label: 'Vaginal atrophy', value: 'vaginal-atrophy' },
    { label: 'Breast cancer', value: 'breast-cancer' },
    { label: 'Uterine cancer', value: 'uterine-cancer' },
    { label: 'Other cancer', value: 'other-cancer' },
    { label: 'Poorly controlled high blood pressure', value: 'poorly-controlled-htn', disqualifies: true },
    { label: 'Low blood pressure', value: 'low-bp', disqualifies: true },
    { label: 'Known coronary artery disease (CAD)/Heart disease', value: 'cad', disqualifies: true },
    { label: 'None of the above', value: 'none' },
  ],
};

const conditionsDetailsQuestion = {
  id: 'conditions-details',
  type: 'textarea',
  question: 'Please provide further information',
  placeholder: 'Tell us more about your condition…',
};

const concernDurationQuestion = {
  id: 'concern-duration',
  type: 'single-select',
  question: 'How long have you been experiencing these concerns?',
  options: [
    { label: 'Less than 3 months', value: 'less-than-3-months' },
    { label: '3 to 6 months', value: '3-to-6-months' },
    { label: 'More than 6 months', value: 'more-than-6-months' },
  ],
};

const distressQuestion = {
  id: 'distress',
  type: 'single-select',
  question: 'Do these concerns cause you distress or affect your relationships?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const sexuallyActiveQuestion = {
  id: 'sexually-active',
  type: 'single-select',
  question: 'Are you currently sexually active?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const lifeChangesQuestion = {
  id: 'life-changes',
  type: 'single-select',
  question: 'Have you experienced any significant life changes or stressors recently?',
  subtitle: 'e.g., relationship changes, job loss, bereavement.',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

// ---------- History / cardiovascular screening ----------

const physicalExamQuestion = {
  id: 'physical-exam',
  type: 'single-select',
  question: 'Have you had a physical exam with a healthcare provider in the past 5 years?',
  options: [
    { label: 'Yes, I am in good health', value: 'yes-good-health' },
    { label: 'Yes, but they found problems', value: 'yes-found-problems' },
    { label: 'No', value: 'no' },
  ],
};

const physicalExamDetailsQuestion = {
  id: 'physical-exam-details',
  type: 'textarea',
  question: 'Please explain any issues during your last physical exam',
  placeholder: 'Describe what was found…',
};

const bpTakenQuestion = {
  id: 'bp-taken',
  type: 'single-select',
  question: 'Was your blood pressure taken in the past year?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No, I will get a new blood pressure measurement', value: 'no-will-measure' },
  ],
};

const bpValueQuestion = {
  id: 'bp-value',
  type: 'text',
  question: 'What was the value?',
  subtitle: 'Example: 115/72',
  placeholder: 'e.g., 115/72',
};

const highBpQuestion = {
  id: 'high-bp',
  type: 'single-select',
  question: 'Do you have high blood pressure?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const bpControlledQuestion = {
  id: 'bp-controlled',
  type: 'single-select',
  question: 'Is your blood pressure typically well controlled?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no', disqualifies: true },
  ],
};

const bpRecentReadingQuestion = {
  id: 'bp-recent-reading',
  type: 'text',
  question: 'Please provide a recent blood pressure reading taken within the last 4-6 weeks',
  placeholder: 'e.g., 118/76',
};

// ---------- PT-141 contraindication screen ----------

const indomethacinNaltrexoneQuestion = {
  id: 'indomethacin-naltrexone',
  type: 'single-select',
  question: 'Are you taking indomethacin or naltrexone?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const takesIndomethacinQuestion = {
  id: 'takes-indomethacin',
  type: 'single-select',
  question: 'Do you take indomethacin?',
  // PINK: 'yes' contraindicates PT-141 (product routing, not a hard reject).
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const takesNaltrexoneQuestion = {
  id: 'takes-naltrexone',
  type: 'single-select',
  question: 'Do you take naltrexone?',
  // PINK: 'yes' contraindicates PT-141 (product routing, not a hard reject).
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const liverKidneyQuestion = {
  id: 'liver-kidney',
  type: 'single-select',
  question: 'Do you have liver cirrhosis or advanced kidney failure?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

// ---------- PDE5i contraindication screens ----------

const nitrateMedsQuestion = {
  id: 'nitrate-meds',
  type: 'multi-select',
  question: 'Do you take any of the following medicines?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Nitroglycerin spray, ointment, patches or tablets', value: 'nitroglycerin', disqualifies: true },
    { label: 'Isosorbide mononitrate, or isosorbide dinitrate (Isordil, Dilatrate, Sorbitrate, Imdur, Ismo, Monoket)', value: 'isosorbide', disqualifies: true },
    { label: 'Other medicines containing nitrates', value: 'other-nitrates', disqualifies: true },
    { label: 'Alpha blocker medications: doxazosin (Cardura), prazosin (Minipress), terazosin (Hytrin)', value: 'alpha-blockers', disqualifies: true },
    { label: 'Riociguat (Adempas)', value: 'riociguat', disqualifies: true },
    { label: 'None apply', value: 'none' },
  ],
};

const cardiovascularSymptomsQuestion = {
  id: 'cardiovascular-symptoms',
  type: 'multi-select',
  question: 'Do you experience any of the following cardiovascular symptoms?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Chest pain or shortness of breath when climbing 2 flights of stairs or walking 4 blocks', value: 'chest-pain-exertion', disqualifies: true },
    { label: 'Chest pain or shortness of breath with sexual activity', value: 'chest-pain-sex', disqualifies: true },
    { label: 'Unexplained fainting or dizziness', value: 'fainting-dizziness', disqualifies: true },
    { label: 'Prolonged cramping of the legs with exercise', value: 'leg-cramping', disqualifies: true },
    { label: 'Abnormal heart beats or rhythms', value: 'arrhythmia', disqualifies: true },
    { label: 'None of these apply to me', value: 'none' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions.',
  placeholder: 'Type your questions or concerns here…',
};

// ---------- Contact / shipping / identity (FE commerce + delivery) ----------

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment.",
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address',
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
  // Required by Beluga — ID photo cannot be skipped (2026-06-27).
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'Use of Medications and Off-Label Prescribing',
  consentText: MAIN_CONSENT_BODY,
  options: [
    { label: 'I have read the above information, I understand the risks, and I would like to proceed.', value: 'consent' },
    { label: 'I have read the above information, and I do NOT wish to proceed.', value: 'decline', disqualifies: true },
  ],
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Select Your Treatment Plan',
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete',
};

// ============================================
// FULL FLOW (canonical questionnaire order)
// ============================================

export const womenSexualHealthFlow = [
  // PURPLE / named-field
  medicalConditionsQuestion,
  selfReportedMedsQuestion,
  allergiesQuestion,
  sexAtBirthQuestion,

  // Pregnancy branch (conditional on female)
  pregnancyStatusQuestion,
  pregnancyConsentQuestion, // conditional: pregnancy-status === 'no'

  heightWeightQuestion,

  // Sexual health concerns
  sexualHealthConcernsQuestion,
  sexualHealthSymptomsDetailsQuestion, // conditional: concerns includes 'other'
  conditionsQuestion,
  conditionsDetailsQuestion, // conditional: conditions includes 'other-cancer'
  concernDurationQuestion,
  distressQuestion,
  sexuallyActiveQuestion,
  lifeChangesQuestion,

  // History / cardiovascular screening
  physicalExamQuestion,
  physicalExamDetailsQuestion, // conditional: physical-exam === 'yes-found-problems'
  bpTakenQuestion,
  bpValueQuestion, // conditional: bp-taken === 'yes'
  highBpQuestion,
  bpControlledQuestion, // conditional: high-bp === 'yes'
  bpRecentReadingQuestion, // conditional: bp-controlled === 'yes'

  // PT-141 contraindication screen
  indomethacinNaltrexoneQuestion,
  takesIndomethacinQuestion, // conditional: indomethacin-naltrexone === 'yes'
  takesNaltrexoneQuestion, // conditional: indomethacin-naltrexone === 'yes'
  liverKidneyQuestion,

  // PDE5i contraindication screens
  nitrateMedsQuestion,
  cardiovascularSymptomsQuestion,

  additionalQuestionsQuestion,

  // Contact / shipping / identity
  basicContactQuestion,
  shippingAddressStep,
  identityVerificationStep,

  mainConsentQuestion,
  checkoutStep,
  completeStep,
];

export const womenSexualHealthFlowById = womenSexualHealthFlow.reduce((acc, slide) => {
  acc[slide.id] = slide;
  return acc;
}, {});

// ============================================
// Dynamic flow builder — strips conditionals that have not fired
// ============================================

export const buildDynamicWomenSexualHealthFlow = (answers) => {
  let flow = [...womenSexualHealthFlow];
  const has = (id) => answers[id] !== undefined && answers[id] !== null;
  const arr = (id) => (Array.isArray(answers[id]) ? answers[id] : []);

  // Pregnancy branch — only for female; consent only when not pregnant.
  if (answers['sex-at-birth'] !== 'female') {
    flow = flow.filter((q) => !['pregnancy-status', 'pregnancy-consent'].includes(q.id));
  } else if (answers['pregnancy-status'] !== 'no') {
    flow = flow.filter((q) => q.id !== 'pregnancy-consent');
  }

  // sexual-health-symptoms-details — only when 'other' selected
  if (!arr('sexual-health-concerns').includes('other')) {
    flow = flow.filter((q) => q.id !== 'sexual-health-symptoms-details');
  }

  // conditions-details — only when 'other-cancer' selected
  if (!arr('conditions').includes('other-cancer')) {
    flow = flow.filter((q) => q.id !== 'conditions-details');
  }

  // physical-exam-details — only when they found problems
  if (answers['physical-exam'] !== 'yes-found-problems') {
    flow = flow.filter((q) => q.id !== 'physical-exam-details');
  }

  // bp-value — only when BP was taken in the past year
  if (answers['bp-taken'] !== 'yes') {
    flow = flow.filter((q) => q.id !== 'bp-value');
  }

  // bp-controlled — only when patient has high blood pressure
  if (answers['high-bp'] !== 'yes') {
    flow = flow.filter((q) => !['bp-controlled', 'bp-recent-reading'].includes(q.id));
  } else if (answers['bp-controlled'] !== 'yes') {
    // bp-recent-reading only when BP is well controlled
    flow = flow.filter((q) => q.id !== 'bp-recent-reading');
  }

  // indomethacin / naltrexone sub-questions — only when the gate is 'yes'
  if (answers['indomethacin-naltrexone'] !== 'yes') {
    flow = flow.filter((q) => !['takes-indomethacin', 'takes-naltrexone'].includes(q.id));
  }

  // (has() referenced to keep lint happy for future conditionals)
  void has;

  return flow;
};

// ============================================
// Per-answer disqualifier resolver
// ============================================

const MULTI_SELECT_DISQUALIFIERS = {
  conditions: ['poorly-controlled-htn', 'low-bp', 'cad'],
  'nitrate-meds': ['nitroglycerin', 'isosorbide', 'other-nitrates', 'alpha-blockers', 'riociguat'],
  'cardiovascular-symptoms': [
    'chest-pain-exertion',
    'chest-pain-sex',
    'fainting-dizziness',
    'leg-cramping',
    'arrhythmia',
  ],
};

const DISQUALIFY_REASONS = {
  'sex-at-birth':
    'This program supports treatment for individuals assigned female at birth. We cannot continue with this submission.',
  'pregnancy-status':
    "These medications may not be safe during pregnancy, breastfeeding, or while planning to conceive, so we can't proceed right now. Please discuss alternatives with your provider.",
  'pregnancy-consent':
    'You have indicated that you do not wish to proceed.',
  'bp-controlled':
    'Because your blood pressure is not well controlled, these medications are not safe for you right now. Please follow up with your primary care provider.',
  'liver-kidney':
    'Based on your medical history, these medications are not safe for you right now. Please discuss alternatives with your primary care provider.',
  conditions:
    'Based on your medical history, these medications are not safe for you right now. Please discuss alternatives with your primary care provider.',
  'nitrate-meds':
    'These medications cannot be combined safely with nitrates, alpha blockers, or riociguat. We cannot proceed for your safety.',
  'cardiovascular-symptoms':
    'Your reported cardiovascular symptoms need to be evaluated in person before these medications can be considered. Please seek care with your provider.',
  'main-consent':
    'You have indicated that you do not wish to continue with this treatment.',
};

export const isDisqualifyingAnswer = (questionId, answer) => {
  // Single-select hard rejects.
  if (questionId === 'sex-at-birth' && answer === 'male') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['sex-at-birth'] };
  }
  if (questionId === 'pregnancy-status' && answer === 'yes') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['pregnancy-status'] };
  }
  if (questionId === 'pregnancy-consent' && answer === 'decline') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['pregnancy-consent'] };
  }
  if (questionId === 'bp-controlled' && answer === 'no') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['bp-controlled'] };
  }
  if (questionId === 'liver-kidney' && answer === 'yes') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['liver-kidney'] };
  }
  if (questionId === 'main-consent' && answer === 'decline') {
    return { disqualified: true, reason: DISQUALIFY_REASONS['main-consent'] };
  }

  // Multi-select hard rejects — any disqualifying option selected.
  if (Array.isArray(answer) && MULTI_SELECT_DISQUALIFIERS[questionId]) {
    const hit = MULTI_SELECT_DISQUALIFIERS[questionId].some((v) => answer.includes(v));
    if (hit) {
      return { disqualified: true, reason: DISQUALIFY_REASONS[questionId] };
    }
  }

  return { disqualified: false };
};

// ============================================
// Product routing — PT-141 contraindication (PINK)
// ============================================
//
// indomethacin or naltrexone contraindicates PT-141 (Bremelanotide) only;
// the patient may still be a candidate for PDE5i or oxytocin. Used by the
// checkout step to hide PT-141 plans and by the submit envelope to keep
// patientPreference consistent.

export const isPt141Contraindicated = (answers) =>
  answers['takes-indomethacin'] === 'yes' || answers['takes-naltrexone'] === 'yes';

// ============================================
// TREATMENT PLANS (placeholder pricing — backend reconciles SKU + medId on
// submission, same pattern as trtPlans). The FE renders these so checkout
// works without an extra round-trip.
// ============================================

// Only products Beluga can actually fill are listed (BE seed 2026-06-03):
// the PT-141 product is a COMBO gummy (PT-141 + oxytocin), not standalone, and
// standalone oxytocin. PDE5i (tadalafil/sildenafil) are NOT in Beluga's catalog
// yet — intentionally omitted; re-add a 'PDE5 Inhibitor' category once Beluga
// seeds those SKUs. Prices are placeholders (reconcile vs Airtable before the
// route serves real patients). All WSH products dispense via pharmacy 271328.
export const womenSexualHealthPlans = [
  {
    category: 'PT-141 + Oxytocin Gummy',
    description: 'Combo gummy (PT-141 + oxytocin) for desire, arousal and intimacy',
    productKey: 'pt141',
    plans: [
      {
        plan_slug: 'wsh-pt141-monthly',
        plan_name: 'PT-141 + Oxytocin Gummy — Monthly',
        plan_price: 149,
        plan_content:
          'Compounded PT-141 + oxytocin combo gummy,Monthly supply,Provider consultation,Free shipping,Secure messaging with provider',
      },
      {
        plan_slug: 'wsh-pt141-quarterly',
        plan_name: 'PT-141 + Oxytocin Gummy — Quarterly',
        plan_price: 399,
        plan_content:
          'Best Value,Compounded PT-141 + oxytocin combo gummy,3-month supply,Provider consultation,Free shipping,Secure messaging with provider',
      },
    ],
  },
  {
    category: 'Oxytocin',
    description: 'Compounded oxytocin to support intimacy and connection',
    productKey: 'oxytocin',
    plans: [
      {
        plan_slug: 'wsh-oxytocin-monthly',
        plan_name: 'Oxytocin — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded oxytocin,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
];

// Product categories contraindicated when PT-141 is excluded.
export const PT141_PRODUCT_KEY = 'pt141';

export default womenSexualHealthFlow;
